import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  BarChart3,
  BookOpen,
  Bot,
  Building2,
  LayoutDashboard,
  Lightbulb,
  LogOut,
  Mail,
  Menu,
  Users,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';

import logo from '../assets/brand/sissi-logo.svg';
import symbol from '../assets/brand/sissi-symbol.svg';
import { useAuth } from '../hooks/useAuth';
import { useIsMobile } from '../hooks/useIsMobile';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetTitle, SheetTrigger } from '@/components/ui/sheet';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';

// Below this width the sidebar becomes a drawer. It is wider than the app's
// 768px "mobile" breakpoint on purpose: at tablet sizes the patient table and
// the detail panels need the 240px back more than the nav needs to be pinned.
export const SIDEBAR_BREAKPOINT = 1024;

interface NavItem {
  id: string;
  label: string;
  // Under the icon in the narrow menu, when the full label does not fit.
  short?: string;
  icon: LucideIcon;
  path: string;
  /** Only an admin sees the item; the menu says so instead of leaving it to guesswork. */
  adminOnly?: boolean;
}

const ADMIN_GROUP_LABEL = 'Administración';

function initials(firstName?: string, lastName?: string, email?: string) {
  if (firstName && lastName) return `${firstName[0]}${lastName[0]}`.toUpperCase();
  if (email) return email[0].toUpperCase();
  return 'U';
}

const Sidebar = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout, refreshUser } = useAuth();
  const isCompact = useIsMobile(SIDEBAR_BREAKPOINT);
  const [drawerOpen, setDrawerOpen] = useState(false);

  // The clinic under the logo comes from the login response, so a session
  // opened before an admin renamed or reassigned the clinic would keep the old
  // one. Refreshed once per load; admins have no clinic to show.
  const isProfessional = user?.role === 'doctor' || user?.role === 'nutritionist';
  useEffect(() => {
    if (isProfessional) void refreshUser();
  }, [isProfessional, refreshUser]);

  // Close the drawer after navigating. Radix restores focus to the trigger and
  // handles the scroll lock, which the old hand-rolled drawer did by mutating
  // document.body.style directly.
  useEffect(() => {
    setDrawerOpen(false);
  }, [location.pathname]);

  const navItems: NavItem[] = [
    { id: 'dashboard', label: 'Inicio', path: '/app', icon: LayoutDashboard },
    { id: 'patients', label: 'Mis pacientes', short: 'Pacientes', path: '/app/patients', icon: Users },
    { id: 'insights', label: 'Insights', path: '/app/insights', icon: Lightbulb },
    { id: 'resources', label: 'Recursos', path: '/app/resources', icon: BookOpen },
    ...(user?.role === 'admin'
      ? [
          { id: 'clinics', label: 'Clínicas', path: '/app/clinics', icon: Building2, adminOnly: true },
          { id: 'metrics', label: 'Métricas', path: '/app/metrics', icon: BarChart3, adminOnly: true },
          { id: 'invites', label: 'Invitaciones', path: '/app/invites', icon: Mail, adminOnly: true },
          // "Ejecuciones del agente" truncates in the 240px sidebar; the page
          // title says it in full.
          { id: 'agent-runs', label: 'Agent trace', path: '/app/agent-runs', icon: Bot, adminOnly: true },
        ]
      : []),
  ];
  const firstAdminId = navItems.find((item) => item.adminOnly)?.id;

  const isActive = (path: string) =>
    path === '/app' ? location.pathname === '/app' : location.pathname.startsWith(path);

  // Docked, the menu is a narrow rail (icon with its label underneath) so the
  // page and the Sissi AI panel fit; the drawer keeps the wide layout.
  const railed = !isCompact;

  const nav = (
    <nav className={cn('flex-1 overflow-y-auto', railed ? 'px-1.5 py-2' : 'p-3')}>
      <ul className="m-0 flex list-none flex-col gap-1 p-0">
        {navItems.map(({ id, label, short, icon: Icon, path, adminOnly }) => {
          const active = isActive(path);
          // The admin-only entries sit under their own heading, so a doctor
          // who never sees them and an admin who does both know why.
          const groupStart = adminOnly && id === firstAdminId;
          const button = railed ? (
            <button
              onClick={() => navigate(path)}
              aria-current={active ? 'page' : undefined}
              aria-label={label}
              className={cn(
                'box-border flex w-full cursor-pointer flex-col items-center gap-1 rounded-control border-0 bg-transparent px-0.5 pt-2 pb-1.5 text-[10.5px] leading-tight transition-colors',
                active
                  ? 'bg-primary-subtle font-semibold text-primary-subtle-foreground'
                  : 'font-medium text-muted-foreground hover:bg-muted hover:text-foreground',
              )}
            >
              <Icon className="size-5 shrink-0" aria-hidden="true" />
              <span className="max-w-full truncate">{short ?? label}</span>
            </button>
          ) : (
            <button
              onClick={() => navigate(path)}
              aria-current={active ? 'page' : undefined}
              className={cn(
                'flex h-(--control-height) w-full cursor-pointer items-center gap-3 rounded-control border-0 bg-transparent px-3 text-[15px] font-medium transition-colors',
                active
                  ? 'bg-primary-subtle text-primary-subtle-foreground font-semibold'
                  : 'text-muted-foreground hover:bg-muted hover:text-foreground',
              )}
            >
              <Icon className="size-5 shrink-0" />
              <span className="truncate">{label}</span>
            </button>
          );

          return (
            <li key={id} className={cn(groupStart && 'mt-2 border-t border-border pt-2')}>
              {groupStart && !railed && (
                <div className="px-3 pt-1 pb-1.5 text-[11px] font-semibold tracking-wide text-muted-foreground uppercase">
                  {ADMIN_GROUP_LABEL}
                </div>
              )}
              {railed && adminOnly ? (
                <Tooltip>
                  <TooltipTrigger asChild>{button}</TooltipTrigger>
                  <TooltipContent side="right">{`${label} · ${ADMIN_GROUP_LABEL}`}</TooltipContent>
                </Tooltip>
              ) : (
                button
              )}
            </li>
          );
        })}
      </ul>

    </nav>
  );

  const clinicName = user?.role !== 'admin' ? user?.clinic?.name : undefined;

  // 64px of content plus the 1px border, the same as the page header
  // (HeaderBar: min-h-16 box-border plus its border), so the two bottom
  // borders meet at the sidebar edge instead of stepping.
  const brand = (
    <div
      className={cn(
        'flex h-16 shrink-0 items-center gap-3 border-b border-border px-5',
        railed && 'justify-center px-0',
      )}
    >
      {/* The horizontal logo is the manual's default for the app. The rail is
       *  too narrow for it (the manual's minimum is 96px), so it shows the
       *  symbol alone, at 32px, above the 24px floor for the two-tone mark.
       *  With a clinic name underneath the logo drops to 32px so both fit. */}
      {railed ? (
        <img src={symbol} alt="Sissi Health" className="size-8 shrink-0" />
      ) : (
        <div className="flex min-w-0 flex-col gap-1">
          <img
            src={logo}
            alt="Sissi Health"
            className={cn('w-auto self-start', clinicName ? 'h-8' : 'h-10')}
          />
          {clinicName && (
            <span className="truncate text-xs leading-4 text-muted-foreground">{clinicName}</span>
          )}
        </div>
      )}
    </div>
  );

  const footer = (
    <div className="border-t border-border p-3">
      <div
        className={cn(
          'mb-2 flex items-center gap-3 rounded-control bg-muted p-2.5',
          railed && 'justify-center bg-transparent p-0',
        )}
      >
        <Avatar className="size-9 shrink-0">
          <AvatarFallback className="bg-primary text-primary-foreground text-xs font-semibold">
            {initials(user?.firstName, user?.lastName, user?.email)}
          </AvatarFallback>
        </Avatar>
        {!railed && (
          <div className="min-w-0 flex-1">
            <div className="truncate text-sm font-semibold">
              {user?.firstName && user?.lastName
                ? `${user.firstName} ${user.lastName}`
                : user?.email}
            </div>
            {user?.role && (
              <div className="text-xs text-muted-foreground capitalize">{user.role}</div>
            )}
          </div>
        )}
      </div>

      {railed ? (
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className="w-full"
              onClick={() => {
                logout();
                navigate('/login');
              }}
              aria-label="Cerrar sesión"
            >
              <LogOut />
            </Button>
          </TooltipTrigger>
          <TooltipContent side="right">Cerrar sesión</TooltipContent>
        </Tooltip>
      ) : (
        <Button
          variant="outline"
          className="w-full"
          onClick={() => {
            logout();
            navigate('/login');
          }}
        >
          <LogOut /> Cerrar sesión
        </Button>
      )}
    </div>
  );

  if (isCompact) {
    return (
      <Sheet open={drawerOpen} onOpenChange={setDrawerOpen}>
        <SheetTrigger asChild>
          <Button
            variant="outline"
            size="icon"
            aria-label="Abrir menú"
            className="fixed top-3 left-3 z-30 bg-card shadow-xs"
          >
            <Menu />
          </Button>
        </SheetTrigger>
        <SheetContent side="left" className="flex w-[280px] flex-col gap-0 p-0">
          <SheetTitle className="sr-only">Navegación</SheetTitle>
          {brand}
          {nav}
          {footer}
        </SheetContent>
      </Sheet>
    );
  }

  return (
    <aside
      aria-label="Navegación principal"
      className={cn(
        'sticky top-0 flex h-screen w-[76px] shrink-0 flex-col border-r border-border bg-card',
      )}
    >
      {brand}
      {nav}
      {footer}
    </aside>
  );
};

export default Sidebar;
